import { DateTime } from 'luxon';

export interface User {
  id: number;
  pin: string;
  gravatarUrl: string;
  name: string;
  email: string;
  createdAt: string; // TODO: convert to datetime!
}

export interface PaymentMethod {
  id: number;
  title: string;
  gateway: string;
  settings?: PaymentMethodSettings;
}

export interface PaymentMethodSettings {
  terminalIp?: string;
}

export interface CollectionInfo {
  id: number;
  title: string;
}

export interface Collection extends CollectionInfo {
  products: Product[];
}

export interface PaginatedCollection {
  id: number;
  title: string;
  products: Product[];
  meta: {
    total: number;
    page: number;
  };
}

export interface OnBoardingTask {
  name: string;
  done: boolean;
  title: string;
  body: string;
}

export interface OnBoarding {
  tasks: OnBoardingTask[];
}

export interface Brand {
  logoUrl: string;
  themeColor?: string;
}

export interface TaxCountry {
  id: number;
  countryCode: string;
  mainTaxRate: number;
}

export interface TaxReduced {
  collectionId: number;
  collectionTitle: string;
  taxRate: number;
}

export interface TaxRateSettings {
  taxCountry: TaxCountry;
  reduced: TaxReduced[];
}

export enum PrinterTypes {
  EPSON_IHUB = 'epson_ihub',
  PRINTERBOX = 'printerbox',
  AIR_PRINT_GENERAL = 'air_print_general'
}

export const FEATURE_FLAGS = {
  SALES_HISTORY: 'sales_history',
  RESERVATIONS: 'reservations',
  ADHOC_PRODUCTS: 'adhoc_products',
  CUSTOMERS: 'customers',
  MONEY_MANAGEMENT: 'money_management',
  COUPONS: 'coupons',
  MAIL_RECEIPTS: 'mail_receipts',
} as const;

export type FeatureFlag = typeof FEATURE_FLAGS[keyof typeof FEATURE_FLAGS];

export interface Settings {
  features?: FeatureFlag[];
  subdomain: string;
  url: string;
  shopLogoUrl: string;
  posUuid: string;
  // only appears when epson printer is selected
  epsonIhubDeviceId?: string;
  printerType: PrinterTypes;
  paperSize: string; // Fixme: maybe type or enum
  paymentMethods: PaymentMethod[];
  collections: CollectionInfo[];
  users: User[];
  onboarding: OnBoarding;
  brand: Brand;
  taxRates: TaxRateSettings;
  // only present if S-Payment POS is a payment option
  sPaymentPosAppId?: string;
  // min app version required to use the POS
  minAppVersion?: string;
}

export interface ProductImage {
  id: number;
  url: string | null;
  // Unused properties
  height: unknown;
  width: unknown;
}

export interface ProductVariant {
  id: number;
  title: string;
  subtitle: string | null;
  description: string;
  vendor: string | null;
  category: string | null;
  price: number;
  compareAtPrice: number | null;
  taxRate: number;
  stock: number | null;
  considersStock: boolean;
  available: boolean;
  code: string;
  option01: unknown | null;
  option02: unknown | null;
  option03: unknown | null;
  images: ProductImage[];
}

export interface Product {
  id: number;
  optionNames: unknown[];
  variants: ProductVariant[];
}

export interface PaginatedProducts {
  products: Product[];
  meta: {
    total: number;
    page: number;
  };
}

export interface CartCoupon {
  code: string | null;
  valid: boolean;
  errors: string[];
  value: number;
}

export interface CustomerByEmail {
  email: string;
  acceptsNewsletter: boolean | string;
}

export interface Address {
  category: string;
  city: string;
  company: string;
  country: string;
  customerId: number;
  draftOrderId: null | unknown; // TODO: only null is in provided examples, probably number
  email: null | string;
  extension: string | null;
  firstname: string | null;
  id: number;
  lastname: string | null;
  locked: boolean;
  option01: string | null;
  option02: string | null;
  option03: string | null;
  orderId: null | number;
  phone: string | null;
  state: string;
  street: string;
  streetnumber: string | null;
  title: string | null;
  zipcode: string | null;
}

export type CartCustomerUser = {
  acceptedOptionalCheckoutCheckbox1: boolean;
  acceptsNewsletter: boolean;
  active: boolean;
  birthday: null | unknown; // TODO: only null is in provided examples, probably string
  checkoutAdditionalCheckboxAccepted: boolean;
  confirmedAt: null | unknown; // TODO: only null is in provided examples, probably date string
  createdOn: string; // FIXME: convert to date if required
  email: string | null;
  gender: null | unknown; // TODO: only null is in provided examples
  id: number;
  mobilePhone: null | string;
  option01: string | null;
  option02: string | null;
  option03: string | null;
  option04: string | null;
  propertiesCount: number;
  registered: boolean;
  riskCheckApproval: null | unknown; // TODO: only null is in provided examples
  shopId: number;
  tagList: null | unknown; // TODO: only null is in provided examples
  updatedOn: string; // TODO: convert to date
  vatIdentificationNumber: null | unknown; // TODO: only null is in provided examples
  firstname: string | null;
  lastname: string | null;
  tags: unknown;
  shippingAddress?: Address;
  billingAddress?: Address;
};

export interface CartItem {
  id: number;
  title: string;
  price: number;
  linePrice: number;
  quantity: number;
  productId: number;
  compareAtPrice: number | null;
  discountAmount: number;
  discountType: 'absolute' | 'relative';
  image: ProductImage | null;
  options: ProductOption[];
}

export interface ProductOption {
  name: string;
  position: number;
  selectedValue: string;
  values: string[];
}

export interface CartTax {
  rate: number;
  amount: number;
  standard?: boolean;
}

export interface Cart {
  id: string;
  channel: string;
  totalPrice: number;
  subtotalPrice: number;
  discount: number;
  shippingCost: number;
  extraPrice: number;
  taxAmount: number;
  taxRate: number;
  taxes: CartTax[];
  customer: CustomerByEmail;
  lineItems: CartItem[];
  note: string | null;
  attrs: object;
  errors: string[];
  coupon: CartCoupon;
  reservationId: number | null;
  isReservation: boolean | null;
}

export interface Barcodes {
  [key: string]: number;
}

export interface CashBox {
  cashBalance: number;
  expense: number;
  reportEposUrl?: string;
  reportImageUrl?: string;
  reportPdfUrl?: string;
  revenue: number;
  status: 'open' | 'closed';
}

export interface FailedPayment {
  status: 'failed';
  error: string;
}

export interface CompletedPayment {
  change?: number;
  invoiceImage: string;
  invoiceUrl: string;
  paymentType: string;
  receiptEposUrl: string;
  receiptImageUrl: string;
  receiptUrl: string;
  status: 'success';
  total: number;
}

export type PaymentResponse = CompletedPayment | FailedPayment;

export interface Pickup {
  id: number;
  code?: string;
  email: string;
  name: string;
  createdAt: DateTime;
  pickupAt: DateTime | null;
  pickedAt?: DateTime | null;
  expiresAt?: DateTime | null;
  isReservation?: boolean | null;
  items: PickupItem[];
}

export interface PickupItem {
  productId: number;
  productCode: string | null;
  productTitle: string | null;
  productQuantity: number;
  productPrice: string | null;
  option01: string | null;
  option02: string | null;
  option03: string | null;
}

export interface PickupsAndReservations {
  pickups: Pickup[];
  meta: {
    total: number;
    page: number;
  };
}

export interface PickupsAndReservationsCount {
  count: number;
}

export interface PickedUpResponse {
  id: number;
  receiptEposUrl: string;
  receiptImageUrl: string;
  receiptUrl: string;
}

export interface PrinterStatus {
  id: string;
  status: string;
  printerState: 'offline' | 'online';
  jobCount: number;
}

export interface AuthorizationResponse {
  status: string;
  transactionId: string;
}

export interface RefundResponse {
  status: string;
  transactionId: string;
}

export interface ReversalResponse {
  status: string;
  transactionId: string;
}

export interface EndOfDayResponse {
  status: string;
  transactionId: string;
}
